import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle, Lock, Eye, EyeOff } from 'lucide-react';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import './CustomerAuth.css';

const CustomerResetPassword: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const { resetPassword } = useCustomerAuth();

  useEffect(() => {
    // Validate token exists
    if (!token) {
      setMessage({ 
        type: 'error', 
        text: 'Invalid password reset link. Please request a new one.' 
      });
    }
  }, [token]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear field error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    // Clear message
    if (message.text) {
      setMessage({ type: '', text: '' });
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || !token) return;

    setIsSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      const result = await resetPassword(token, formData.password);
      if (result.success) {
        setMessage({ 
          type: 'success', 
          text: 'Password has been reset successfully! Redirecting to login...' 
        });
        setTimeout(() => navigate('/login'), 3000);
      } else {
        setMessage({ type: 'error', text: result.message });
      }
    } catch (error: any) {
      setMessage({ 
        type: 'error', 
        text: error.message || 'Failed to reset password. Please try again.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="partner-login+">
      <div className="row m-0 align-items-center">
        <div className="col-md-5 p-0">
          <div className="left-login">
            <img className="left-log" src="/assets/images/login-bk.png" alt=""/>
          </div>
        </div>
        <div className="col-md-7 p-0">
          <div className="right-login">
            <div className="innerpart-form">
              <div className="customer-auth-card">
                <div className="auth-header">
                  <div className="auth-icon">
                    <Lock size={32} />
                  </div>
                  <h1>Reset Password</h1>
                  <p>Enter your new password</p>
                </div>

                {message.text && (
                  <div className={`auth-message ${message.type}`}>
                    {message.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
                    <span>{message.text}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="auth-form">
                  <div className="form-group">
                    <span className='ic'><img src='/assets/images/lock-n.png' alt=""/></span>
                    <label htmlFor="password">New Password</label>
                    <div className="input-wrapper">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder="Enter your new password"
                        className={errors.password ? 'error' : ''}
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {errors.password && <span className="error-text">{errors.password}</span>}
                  </div>

                  <div className="form-group">
                    <span className='ic'><img src='/assets/images/lock-n.png' alt=""/></span>
                    <label htmlFor="confirmPassword">Confirm Password</label>
                    <div className="input-wrapper">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        placeholder="Confirm your new password"
                        className={errors.confirmPassword ? 'error' : ''}
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
                  </div>

                  <button
                    type="submit"
                    className="auth-button"
                    disabled={isSubmitting || message.type === 'success'}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="loading-spinner small" />
                        Resetting Password...
                      </>
                    ) : 'Reset Password'}
                  </button>
                </form>

                <div className="auth-footer">
                  <p>
                    Remember your password?{' '}
                    <Link to="/login" className="auth-link">
                      Sign in here
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CustomerResetPassword; 